import { createServerFn } from "@tanstack/react-start";
import { createAuthenticatedApiConfig, throwApiError } from "@/lib/utils.server";
import { isForbiddenError } from "@/lib/utils";
import {
  createBusiness,
  createCategory,
  createType,
  deleteCategory,
  deleteType,
  deleteBusiness,
  getBusiness,
  getBusinesses,
  getCategories,
  getCategory,
  getType,
  getTypes,
  updateBusiness,
  updateCategory,
  updateType,
  getRegions,
  getLanguages,
} from "@/lib/mma";
import {
  BusinessListViewReadable,
  BusinessReadable,
  TypeReadable,
  CategoryReadable,
  BusinessWritable,
  TypeWritable,
  CategoryWritable,
  GetRegionsResponse,
  GetLanguagesResponse,
} from "@/lib/mma/types.gen";

// BUSINESSES

export const getBusinessesFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<BusinessListViewReadable[]> => {
    const config = await createAuthenticatedApiConfig();

    const response = await getBusinesses(config);
    if (!response.data) {
      return await throwApiError(response, "Failed to get businesses", response.response?.status);
    }
    return response.data?.data || [];
  }
);

export const businessesQueryOptions = {
  queryKey: ["businesses"],
  queryFn: getBusinessesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
};

export const getBusinessFn = createServerFn({ method: "GET" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<BusinessReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getBusiness({ ...config, path: { id: data.id } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get business", response.response?.status);
    }
    return response.data?.data || {};
  });

export const businessQueryOptions = (id: number) => ({
  queryKey: ["business", id],
  queryFn: () => getBusinessFn({ data: { id } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  enabled: id > 0,
});

export const createBusinessFn = createServerFn({ method: "POST" })
  .inputValidator((data: { payload: BusinessWritable }) => data)
  .handler(async ({ data }): Promise<BusinessReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createBusiness({ ...config, body: data.payload });
    if (!response.data) {
      return await throwApiError(response, "Failed to create business", response.response?.status);
    }
    return response.data?.data || {};
  });

export const updateBusinessFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number; payload: BusinessWritable }) => data)
  .handler(async ({ data }): Promise<BusinessReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateBusiness({ ...config, path: { id: data.id }, body: data.payload });
    if (!response.data) {
      return await throwApiError(response, "Failed to update business", response.response?.status);
    }
    return response.data?.data || {};
  });

export const deleteBusinessFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteBusiness({ ...config, path: { id: data.id } });
    if ((response as any).response.status !== 204) {
      return await throwApiError(response, "Failed to delete business", response.response?.status);
    }
    return { success: true };
  });

// BUSINESS TYPES

export const getBusinessTypesFn = createServerFn({ method: "GET" }).handler(async (): Promise<TypeReadable[]> => {
  const config = await createAuthenticatedApiConfig();

  const response = await getTypes(config);
  if (!response.data) {
    return await throwApiError(response, "Failed to get types", response.response?.status);
  }
  return response.data?.data || [];
});

export const businessTypesQueryOptions = {
  queryKey: ["businessTypes"],
  queryFn: getBusinessTypesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
};

export const getBusinessTypeFn = createServerFn({ method: "GET" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<TypeReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getType({ ...config, path: { id: data.id } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get type", response.response?.status);
    }
    return response.data?.data || {};
  });

export const businessTypeQueryOptions = (id: number) => ({
  queryKey: ["businessType", id],
  queryFn: () => getBusinessTypeFn({ data: { id } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
});

export const createBusinessTypeFn = createServerFn({ method: "POST" })
  .inputValidator((data: { payload: TypeWritable }) => data)
  .handler(async ({ data }): Promise<TypeReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createType({ ...config, body: data.payload });
    if (!response.data) {
      return await throwApiError(response, "Failed to create business type", response.response?.status);
    }
    return response.data?.data || {};
  });

export const updateBusinessTypeFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number; payload: TypeWritable }) => data)
  .handler(async ({ data }): Promise<TypeReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateType({ ...config, path: { id: data.id }, body: data.payload });
    if (!response.data) {
      return await throwApiError(response, "Failed to update business type", response.response?.status);
    }
    return response.data?.data || {};
  });

export const deleteBusinessTypeFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteType({ ...config, path: { id: data.id } });
    if ((response as any).response.status !== 204) {
      return await throwApiError(response, "Failed to delete business type", response.response?.status);
    }
    return { success: true };
  });

// BUSINESS CATEGORIES

export const getBusinessCategoriesFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<CategoryReadable[]> => {
    const config = await createAuthenticatedApiConfig();

    const response = await getCategories(config);
    if (!response.data) {
      return await throwApiError(response, "Failed to get categories", response.response?.status);
    }
    return response.data?.data || [];
  }
);

export const businessCategoriesQueryOptions = {
  queryKey: ["businessCategories"],
  queryFn: getBusinessCategoriesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
};

export const getBusinessCategoryFn = createServerFn({ method: "GET" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<CategoryReadable> => {
    const config = await createAuthenticatedApiConfig();

    const response = await getCategory({ ...config, path: { id: data.id } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get category", response.response?.status);
    }
    return response.data?.data || {};
  });

export const businessCategoryQueryOptions = (id: number) => ({
  queryKey: ["businessCategory", id],
  queryFn: () => getBusinessCategoryFn({ data: { id } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  enabled: id > 0,
});

export const createBusinessCategoryFn = createServerFn({ method: "POST" })
  .inputValidator((data: { payload: CategoryWritable }) => data)
  .handler(async ({ data }): Promise<CategoryReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createCategory({ ...config, body: data.payload });
    if (!response.data) {
      return await throwApiError(response, "Failed to create business category", response.response?.status);
    }
    return response.data?.data || {};
  });

export const updateBusinessCategoryFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number; payload: CategoryWritable }) => data)
  .handler(async ({ data }): Promise<CategoryReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateCategory({ ...config, path: { id: data.id }, body: data.payload });
    if (!response.data) {
      return await throwApiError(response, "Failed to update business category", response.response?.status);
    }
    return response.data?.data || {};
  });

export const deleteBusinessCategoryFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteCategory({ ...config, path: { id: data.id } });
    if ((response as any).response.status !== 204) {
      return await throwApiError(response, "Failed to delete business category", response.response?.status);
    }
    return { success: true };
  });

// TODO: Re-enable once backend is ready to provide affected businesses list
// export const getAffectedBusinessNamesByCategoryFn = createServerFn({ method: "GET" })
//   .inputValidator((data: { categoryId: number }) => data)
//   .handler(async ({ data }): Promise<string[]> => {
//     const config = await createAuthenticatedApiConfig();
//     const listResponse = await getBusinesses(config);
//     if (!listResponse.data) {
//       throwApiError(listResponse, "Failed to get businesses", listResponse.response?.status);
//     }
//     const businessList = listResponse.data?.data || [];
//     const allIds = businessList.map((b: any) => b.id).filter(Boolean) as number[];
//
//     // Fetch full business details to get categoryId
//     const results = await Promise.allSettled(allIds.map((id) => getBusiness({ ...config, path: { id } })));
//
//     const fullBusinesses = results
//       .map((result) => {
//         if (result.status === "fulfilled" && result.value?.data?.data) {
//           const business = result.value.data.data;
//           return business;
//         }
//         if (result.status === "rejected") {
//           console.error("Failed to fetch business:", result.reason);
//         }
//         return null;
//       })
//       .filter((b): b is any => b !== null);
//
//     const affected = fullBusinesses
//       .filter((b: any) => b.categoryId === data.categoryId)
//       .map((b: any) => b.name ?? `Business ${b.id}`);
//
//     return affected;
//   });
//
// export const getAffectedBusinessNamesByTypeFn = createServerFn({ method: "GET" })
//   .inputValidator((data: { typeId: number }) => data)
//   .handler(async ({ data }): Promise<string[]> => {
//     const config = await createAuthenticatedApiConfig();
//     const listResponse = await getBusinesses(config);
//     if (!listResponse.data) {
//       throwApiError(listResponse, "Failed to get businesses", listResponse.response?.status);
//     }
//     const businessList = listResponse.data?.data || [];
//     const allIds = businessList.map((b: any) => b.id).filter(Boolean) as number[];
//
//     // Fetch full business details to get typeId
//     const results = await Promise.allSettled(allIds.map((id) => getBusiness({ ...config, path: { id } })));
//
//     const fullBusinesses = results
//       .map((result) => {
//         if (result.status === "fulfilled" && result.value?.data?.data) {
//           const business = result.value.data.data;
//           return business;
//         }
//         if (result.status === "rejected") {
//           console.error("Failed to fetch business:", result.reason);
//         }
//         return null;
//       })
//       .filter((b): b is any => b !== null);
//
//     const affected = fullBusinesses
//       .filter((b: any) => b.typeId === data.typeId)
//       .map((b: any) => b.name ?? `Business ${b.id}`);
//
//     return affected;
//   });

export const getRegionsFn = createServerFn({ method: "GET" }).handler(async (): Promise<string[]> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getRegions(config);
  if (!response.data) {
    return await throwApiError(response, "Failed to get regions", response.response?.status);
  }
  return response.data?.data || [];
});

export const regionsQueryOptions = {
  queryKey: ["regions"],
  queryFn: getRegionsFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
};

export const getLanguagesFn = createServerFn({ method: "GET" }).handler(async (): Promise<string[]> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getLanguages(config);
  if (!response.data) {
    return await throwApiError(response, "Failed to get languages", response.response?.status);
  }
  return response.data?.data || [];
});

export const languagesQueryOptions = {
  queryKey: ["languages"],
  queryFn: getLanguagesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
};
